import { StateService } from "@uirouter/angularjs";
import { IPromise, IOnInit } from "angular";

import { IQuery } from "../../../../records/server/query";
import {
  IFieldArray,
  IFieldGroup,
  IFieldRuntimeObject,
  ITemplateScope,
} from "AngularFormly";
import { FormViewController } from "../../../../../app/records/forms/formView/controller";
import { set } from "lodash";

import {
  FormViewService,
  vsName as FormViewServiceName,
} from "../../../../../app/records/forms/formView/service";
import fieldHelpers from "../../../../../app/ui-formly/fieldHelpers";

import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../../app/records/forms/formly/fieldConfigurationHelpers";

import {
  ProcessGroup,
  ProcessInformation,
} from "../../../../Process/model/ProcessGroups";
import {
  SelectRecordModalService,
  vsServiceName as SelectRecordModalServiceName,
} from "../../../../records/services/selectRecordModal";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../../records/views/recordGridView";

import { vsObjectName as slotcheckEmailTemplateName } from "../../../config/objects/slotcheckEmailTemplate";

export class ProcessNotificationEditController implements IOnInit {
  static $inject = [
    FormViewServiceName,
    "$state",
    FieldConfigurationHelperServiceName,
    SelectRecordModalServiceName,
    RecordGridViewServiceName,
  ];

  readonly defaultQuery: IQuery = {};

  model: any;

  userFormFields: IFieldArray = [];

  action!: any;
  actionId: string = "";

  selectedProcess: ProcessInformation | undefined;
  selectedGroup: ProcessGroup | undefined;

  private processGroups!: ProcessGroup[];

  constructor(
    private readonly formViewService: FormViewService,
    private readonly $state: StateService,
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly selectRecordModalService: SelectRecordModalService,
    private readonly recordGridViewService: RecordGridViewService
  ) {}

  private initializeUserFormFields(processGroups: ProcessGroup[]) {
    const that = this;

    const recipientFields: IFieldGroup = {
      className: "row",
      fieldGroup: [
        {
          key: "RecipientType",
          name: "RecipientType",
          type: "select",
          className: "col-xs-3",
          templateOptions: {
            label: "Type",
            options: [
              {
                label: "Email",
                value: "Email",
              },
              {
                label: "Role",
                value: "Role",
              },
              {
                label: "User",
                value: "User",
              },
            ],
            required: true,
            labelProp: "label",
            valueProp: "value",
          },
        },
        fieldHelpers.simpleStringField({
          key: "UserEmail",
          name: "UserEmail",
          label: "User Email",
          hideExpression:
            "!model.RecipientType || model.RecipientType !== 'Email'",
          watcher: [that.nullWhenFalse("model.RecipientType === 'Email'")],
          className: "col-xs-3",
        }),
        that.fieldConfigurationHelperService.selectAsyncField({
          key: "UserRoleId",
          name: "UserRoleId",
          label: "Role",
          queryUrl: "/api/role",
          className: "col-xs-3",
          hideExpression:
            "!model.RecipientType || model.RecipientType !== 'Role'",
          getDefaultItem: function (model: any) {
            return {
              Id: model.UserRole?.Id,
              Name: model.UserRole?.RoleName,
            };
          },
          watcher: [
            that.nullWhenFalse("model.RecipientType === 'Role'"),
            {
              expression: "model.RecipientType === 'Role'",
              listener(_, isRole, __, scope) {
                if (!isRole) {
                  (<any>scope).model.UserRole = undefined;
                }
              },
            },
          ],
          onSelect(item, model) {
            (<any>model).UserRole = item;
          },
        }),
        this.fieldConfigurationHelperService.selectUserField({
          key: "UserAuthId",
          name: "UserAuthId",
          label: "Users",
          className: "col-xs-3",
          hideExpression:
            "!model.RecipientType || model.RecipientType !== 'User'",
          initialItemSelector(model: any) {
            return model.UserAuth;
          },
          watcher: [
            that.nullWhenFalse("model.RecipientType === 'User'"),
            {
              expression: "model.RecipientType === 'User'",
              listener(_, isUser, __, scope) {
                if (!isUser) {
                  (<any>scope).model.UserAuth = undefined;
                }
              },
            },
          ],
          onSelect(item, model) {
            model.UserAuth = item;
          },
        }),
        {
          key: "copyDelete",
          type: "recordRepeaterCopyDelete",
          templateOptions: {
            label: " ",
            allowMove: false,
          },
          className: "col-xs-6",
        },
      ],
    };

    this.userFormFields = [
      fieldHelpers.headerField({
        headerText: "Action",
        key: "ActionHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          {
            key: "GroupId",
            name: "GroupId",
            type: "select",
            templateOptions: {
              label: "Group",
              options: processGroups,
              required: true,
              labelProp: "Name",
              valueProp: "Id",
              onChange: (viewValue) => {
                that.selectedGroup = processGroups.find(
                  (g) => g.Id === viewValue
                );
              },
            },
          },
          {
            key: "ProcessId",
            name: "ProcessId",
            type: "select",
            templateOptions: {
              label: "Process Type",
              options: [],
              required: true,
              labelProp: "DisplayName",
              valueProp: "Id",
              onChange: (viewValue) =>
                (that.selectedProcess =
                  that.selectedGroup?.ProcessInformation.find(
                    (i) => i.Id === viewValue
                  )),
            },
            watcher: [
              {
                expression: "model.GroupId",
                listener: (field: IFieldRuntimeObject) => {
                  const processGroup = that.selectedGroup;
                  const newOptions = processGroup?.ProcessInformation ?? [];

                  const options = field.templateOptions?.options ?? [];

                  options.splice(0, options.length);
                  options.push(...newOptions);
                  field.value = undefined;
                },
              },
            ],
          },
          {
            key: "StateId",
            name: "StateId",
            type: "select",
            templateOptions: {
              label: "State",
              required: true,
              labelProp: "Name",
              valueProp: "Id",
              options: [],
            },
            watcher: [
              {
                expression: "model.ProcessId",
                listener: (field) => {
                  const process = that.selectedProcess;
                  const newOptions = process?.States ?? [];
                  const options = field.templateOptions?.options ?? [];

                  options.splice(0, options.length);
                  options.push(...newOptions);
                },
              },
            ],
          },
          {
            key: "NotificationType",
            name: "NotificationType",
            type: "select",
            templateOptions: {
              label: "On Action",
              options: [
                {
                  value: "Entry",
                },
                {
                  value: "Exit",
                },
              ],
              required: true,
              labelProp: "value",
              valueProp: "value",
            },
          },
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Template",
        key: "TemplateHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "SlotcheckEmailTemplateId",
            name: "SlotcheckEmailTemplateId",
            label: "Email Template",
            getDefaultItem(m) {
              return {
                Id: m.SlotcheckEmailTemplate?.Id,
                Name: m.SlotcheckEmailTemplate?.Name,
              };
            },
            queryUrl: "/api/process/template",
            addonRight: this.selectRecordModalService.getAddon(
              this.recordGridViewService.getGridConfiguration(
                slotcheckEmailTemplateName
              )!,
              (item, model) => {
                model.SlotcheckEmailTemplate = item;
                model.SlotcheckEmailTemplateId = item.Id;
              }
            ),
          }),
        ],
        2
      ),
      fieldHelpers.headerField({
        headerText: "Recipients",
        key: "RecipientHeader",
      }),
      this.fieldConfigurationHelperService.repeaterField({
        key: "NotificationRecipients",
        field: "NotificationRecipients",
        label: "Recipients",
        allowAdd: true,
        allowAddBottom: false,
        displayNameSingular: "Recipient",
        repeaterFields: [recipientFields],
        showHeader: true,
      }),
    ];
  }

  $onInit(): void {
    const processGroups = this.processGroups;
    const selectedProcessGroup = (this.selectedGroup = processGroups.find(
      (g) => g.Id === this.model.GroupId
    ));
    this.selectedProcess = selectedProcessGroup?.ProcessInformation?.find(
      (pi) => pi.Id === this.model.ProcessId
    );
    this.initializeUserFormFields(processGroups);
  }

  submitUser(formViewController: FormViewController): IPromise<any> {
    return this.formViewService
      .submitForm({
        formObject: this.model,
        method: this.model.Id ? "put" : "post",
        formViewController,
        url:
          "/api/process/notification" +
          (this.model.Id ? `/${this.model.Id}` : ""),
      })
      .then(() => this.$state.go("^"));
  }

  cancel() {
    return this.$state.go("^");
  }

  private nullWhenFalse(watch: string) {
    return {
      expression: watch,
      listener: (
        field: IFieldRuntimeObject,
        newValue: any,
        _: any,
        scope: ITemplateScope
      ) => {
        if (!newValue) {
          const path = field.key ?? "";
          set(scope.model ?? {}, path, undefined);
        }
      },
    };
  }
}
